import React, { useState, useEffect } from "react";
import BASE_URL from "../../../config/config";
import { useNavigate } from "react-router-dom";

// ترجمه وضعیت‌ها
function translateStatus(status) {
  switch (status) {
    case "pending": return "در انتظار پرداخت";
    case "paid": return "پرداخت شده";
    case "scheduled": return "زمان‌بندی شده";
    case "completed": return "تکمیل شده";
    case "cancelled": return "لغو شده";
    default: return status;
  }
}

// رنگ‌بندی وضعیت‌ها
function getStatusColor(status) {
  switch (status) {
    case "cancelled": return "error";
    case "completed": return "success";
    case "pending": return "warning";
    case "paid": return "info";
    case "scheduled": return "primary";
    default: return "secondary";
  }
}

const ConsultationListManagement = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // صفحه‌بندی
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // جستجو و فیلتر
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searching, setSearching] = useState(false);

  const [csrfToken, setCsrfToken] = useState("");
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  // دریافت CSRF Token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const res = await fetch(`${BASE_URL}/csrf-token`, { credentials: "include" });
        const data = await res.json();
        setCsrfToken(data.csrfToken);
      } catch {
        setError("دریافت توکن امنیتی با خطا مواجه شد");
      }
    };
    fetchToken();
  }, []);

  // دریافت لیست درخواست‌ها
  useEffect(() => {
    if (csrfToken) fetchConsultations();
    // eslint-disable-next-line
  }, [csrfToken, currentPage, searchQuery, statusFilter]);

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      let url = `${BASE_URL}/admin/consultations?page=${currentPage}&limit=${itemsPerPage}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("خطا در دریافت درخواست‌های مشاوره");

      const data = await response.json();
      setConsultations(data.data.orders || []);
      setTotalPages(data.data.pagination?.totalPages || 1);
      setLoading(false);
      setSearching(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
    setSearching(true);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const handleEdit = (consultation) => {
    navigate(`/admin/consultations/edit/${consultation.id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("آیا از حذف این درخواست مطمئن هستید؟")) return;

    try {
      const res = await fetch(`${BASE_URL}/admin/consultation/${id}`, {
        method: "DELETE",
        headers: {
          "CSRF-Token": csrfToken,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const data = await res.json();
      if (res.ok) {
        setConsultations(prev => prev.filter(c => c.id !== id));
        setMessage("✅ درخواست با موفقیت حذف شد");
        setTimeout(() => setMessage(""), 2500);
      } else {
        setMessage("❌ " + (data.message || "خطا در حذف درخواست"));
      }
    } catch {
      setMessage("❌ خطا در برقراری ارتباط با سرور");
    }
  };

  // ماسک کردن شماره تلفن و ایمیل
  const maskPhone = (phone) => {
    if (!phone || phone.length < 4) return phone;
    return phone.slice(0, 4) + "****" + phone.slice(-3);
  };

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email;
    const [user, domain] = email.split("@");
    return user.slice(0, 2) + "***@" + domain;
  };

  return (
    <div className="consultation-list-container">
      <div className="consultation-list-header">
        <h2>لیست درخواست‌های مشاوره</h2>
      </div>

      {/* جستجو و فیلتر */}
      <div className="consultation-search-box">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="جستجو بر اساس نام، تلفن، ایمیل یا کد پیگیری..."
          />
          <select 
            value={statusFilter} 
            onChange={e => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">همه وضعیت‌ها</option>
            <option value="pending">در انتظار</option>
            <option value="paid">پرداخت شده</option>
            <option value="scheduled">زمان‌بندی شده</option>
            <option value="completed">تکمیل شده</option>
            <option value="cancelled">لغو شده</option>
          </select>
          <button type="submit">جستجو</button>
          {(searchQuery || statusFilter) && (
            <button type="button" onClick={clearSearch}>پاک کردن</button>
          )}
        </form>
      </div>

      {message && (
        <div className={`alert ${message.includes("✅") ? "alert-success" : "alert-danger"}`}>
          {message}
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {loading ? (
        <div className="loading">در حال بارگذاری...</div>
      ) : consultations.length === 0 ? (
        <div className="no-consultations-found">درخواستی وجود ندارد.</div>
      ) : (
        <div className="table-responsive">
          <table className="consultations-table">
            <thead>
              <tr>
                <th>#</th>
                <th>نام</th>
                <th>تلفن</th>
                <th>ایمیل</th>
                <th>مبلغ</th>
                <th>وضعیت</th>
                <th>کد پیگیری</th>
                <th>تاریخ ثبت</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {consultations.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.full_name}</td>
                  <td>{maskPhone(item.phone)}</td>
                  <td>{maskEmail(item.email)}</td>
                  <td>
                    <span className="price-badge">
                      {Number(item.amount).toLocaleString()} تومان
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusColor(item.status)}`}>
                      {translateStatus(item.status)}
                    </span>
                  </td>
                  <td>
                    {item.zarinpal_ref_id ? (
                      <code>{item.zarinpal_ref_id}</code>
                    ) : (
                      <span style={{ color: "#999" }}>—</span>
                    )}
                  </td>
                  <td>
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString("fa-IR")
                      : "—"}
                  </td>
                  <td>
                    <div className="consultation-actions">
                      <button
                        className="edit-consultation-btn"
                        onClick={() => handleEdit(item)}
                        title="ویرایش"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="delete-consultation-btn"
                        onClick={() => handleDelete(item.id)}
                        title="حذف"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* صفحه‌بندی */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            قبلی
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={currentPage === i + 1 ? "active" : ""}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            بعدی
          </button>
        </div>
      )}
    </div>
  );
};

export default ConsultationListManagement;
